import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { IS_DESKTOP_EXPERIENCE } from '../lib/deviceGate'
import { MOTION } from '../lib/motion'
import {
  advance,
  getOpeningStep,
  prefersReducedOpeningMotion,
  startOpeningSequence,
  subscribeOpening,
} from '../lib/openingSequence'
import FlatShell from './FlatShell'
import MayaMenubar from './MayaMenubar'
import PlaylistPlayer from './PlaylistPlayer'
import Sidebar from './Sidebar'
import ThemeSelect from './ThemeSelect'
import ThemeColorTuner from './ThemeColorTuner'
import WelcomeScreen from './WelcomeScreen'
import { IS_OWNER } from '../lib/ownerMode'

const INTRO_STAGGER_MS = MOTION.ms.introStagger
const MAIN_INTRO_DELAY_MS = INTRO_STAGGER_MS * 4
const DOCK_INTRO_DELAY_MS = INTRO_STAGGER_MS * 3
const DIVIDER_INTRO_DELAY_MS = INTRO_STAGGER_MS * 2
const CHROME_FALLBACK_MS = MOTION.ms.introStagger * 4 + 100

const SiteShell = () => {
  const location = useLocation()
  const mainScrollRef = useRef(null)
  const mainInnerRef = useRef(null)
  const openingStep = useSyncExternalStore(
    subscribeOpening,
    getOpeningStep,
    getOpeningStep
  )

  useLayoutEffect(() => {
    startOpeningSequence()
  }, [])

  useEffect(() => {
    if (!IS_DESKTOP_EXPERIENCE) return undefined
    if (openingStep !== 'chrome') return undefined

    if (prefersReducedOpeningMotion()) {
      advance('chrome')
      return undefined
    }

    const mainInner = mainInnerRef.current
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      advance('chrome')
    }

    const onAnimationStart = (event) => {
      if (event.target !== mainInner) return
      if (event.animationName && event.animationName !== 'site-slide-in') return
      finish()
    }

    mainInner?.addEventListener('animationstart', onAnimationStart)
    const timer = window.setTimeout(finish, CHROME_FALLBACK_MS)
    return () => {
      mainInner?.removeEventListener('animationstart', onAnimationStart)
      window.clearTimeout(timer)
    }
  }, [openingStep])

  useLayoutEffect(() => {
    if (!IS_DESKTOP_EXPERIENCE) return
    const mainScroll = mainScrollRef.current
    if (mainScroll) mainScroll.scrollTop = 0
    window.scrollTo(0, 0)
  }, [location.pathname])

  if (!IS_DESKTOP_EXPERIENCE) return <FlatShell />

  const skipHref =
    location.pathname !== '/about' && !location.pathname.endsWith('/details')
      ? '#hub-active-slide'
      : '#main'

  return (
    <>
      <a className="skip-link" href={skipHref}>Skip to content</a>
      <WelcomeScreen />
      <div
        className="relative min-h-screen text-text-secondary grid h-screen grid-cols-[var(--site-sidebar-width)_1fr] overflow-hidden"
      >
        <MayaMenubar />
        <div className="site-sidebar-scroll relative z-[var(--z-page)] col-start-1 sticky top-0 flex min-h-0 h-[calc(100vh-var(--site-dock-height))] flex-col overflow-hidden overflow-x-hidden">
          <Sidebar introStaggerMs={INTRO_STAGGER_MS} />
        </div>
        <div
          ref={mainScrollRef}
          className="site-main-scroll relative z-[var(--z-page)] border-t border-border col-start-2 sticky top-[var(--app-chrome-height,0px)] h-[calc(100vh-var(--site-dock-height)-var(--app-chrome-height,0px))] min-h-0 overflow-x-hidden overflow-y-auto border-t-0"
        >
          <main id="main">
            <div className="site-main pb-6">
              <div
                ref={mainInnerRef}
                className="site-main-inner"
                style={{ '--intro-delay': `${MAIN_INTRO_DELAY_MS}ms` }}
              >
                <Outlet />
              </div>
            </div>
          </main>
        </div>

        <div
          className="site-layout-divider site-intro-item pointer-events-none fixed top-0 z-[var(--z-page)] w-px bg-border bottom-[var(--site-dock-height)] left-[var(--site-sidebar-width)] block"
          style={{ '--intro-delay': `${DIVIDER_INTRO_DELAY_MS}ms` }}
          aria-hidden="true"
        />
      </div>

      <div className="site-bottom-dock fixed bottom-0 left-0 right-0 z-[var(--z-dock)]">
        <div
          className="site-dock-line site-intro-fade"
          aria-hidden="true"
          style={{ '--intro-delay': `${DIVIDER_INTRO_DELAY_MS}ms` }}
        />
        <div
          className="site-intro-dock grid h-[var(--site-dock-height)] grid-cols-[var(--site-sidebar-width)_1fr]"
          style={{ '--intro-delay': `${DOCK_INTRO_DELAY_MS}ms` }}
        >
          <div className="site-dock-theme flex items-center justify-center px-4 sm:px-5 md:px-6">
            <ThemeSelect />
          </div>
          <div className="site-dock-player flex min-w-0 items-center px-3 md:px-8">
            <PlaylistPlayer />
          </div>
        </div>
      </div>

      <ThemeColorTuner showOwnerControls={IS_OWNER} />
    </>
  )
}

export default SiteShell
